import React from "react";
import { Card } from "primereact/card";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useQuery } from "@tanstack/react-query";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import adminService from "../../services/admin/admin.services";
import { Panel } from "primereact/panel";
import { ResponseCounts } from "../../services/forms/form.dto";

const SurveyResults = () => {
  const {
    data: surveyResults,
    isLoading: surveyResponseLoading
  } = useQuery({
    queryKey: ["surveyResults"],
    queryFn: async () => await adminService.GetSurveyResults()
  });

  if (surveyResponseLoading) {
    return (
      <div className="flex justify-content-center">
        <ProgressSpinner />
      </div>
    );
  }

  const renderResponseCounts = (counts: ResponseCounts) => {
    return (
      <div className="grid">
        {Object.entries(counts).map(([response, count]) => (
          <div key={response} className="col-12 md:col-6 lg:col-4">
            <div className="p-2 border-1 surface-border border-round">
              <strong>Cevap:</strong> {response}<br />
              <strong>Sayı:</strong> {count}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-column gap-3">
      <Card title="Anket Sonuçları" className="w-full">
        <TabView>
          <TabPanel header="Karbon Ayak İzi">
            <div className="flex flex-column gap-3">
              <Panel header="İstatistikler">
                <div className="grid">
                  <div className="col-12 md:col-6 lg:col-3">
                    <div className="p-2 border-1 surface-border border-round">
                      <strong>Toplam Yanıt:</strong> {surveyResults?.karbon_ayak_izi.total_responses}
                    </div>
                  </div>
                  <div className="col-12 md:col-6 lg:col-3">
                    <div className="p-2 border-1 surface-border border-round">
                      <strong>Ortalama Araç Sayısı:</strong> {surveyResults?.karbon_ayak_izi.avg_arac_sayisi.toFixed(2)}
                    </div>
                  </div>
                  <div className="col-12 md:col-6 lg:col-3">
                    <div className="p-2 border-1 surface-border border-round">
                      <strong>Ortalama Alınan Araç:</strong> {surveyResults?.karbon_ayak_izi.avg_arac_alinan.toFixed(2)}
                    </div>
                  </div>
                  <div className="col-12 md:col-6 lg:col-3">
                    <div className="p-2 border-1 surface-border border-round">
                      <strong>Ortalama Faal Ömür:</strong> {surveyResults?.karbon_ayak_izi.avg_faal_omur.toFixed(2)}
                    </div>
                  </div>
                </div>
              </Panel>

              <Panel header="Yanıtlar">
                <DataTable value={surveyResults?.karbon_ayak_izi.responses} showGridlines stripedRows paginator rows={10}>
                  <Column field="user_name" header="Kullanıcı"></Column>
                  <Column field="user_osb" header="OSB"></Column>
                  <Column field="arac_sayisi" header="Araç Sayısı"></Column>
                  <Column field="arac_sayisi_alinan" header="Alınan Araç"></Column>
                  <Column field="arac_faal_omur" header="Faal Ömür"></Column>
                  <Column field="arac_pert_miktari" header="Pert Miktarı"></Column>
                  <Column field="created_at" header="Tarih"></Column>
                </DataTable>
              </Panel>
            </div>
          </TabPanel>

          <TabPanel header="Raporlama">
            <div className="flex flex-column gap-3">
              <Panel header="İstatistikler">
                <div className="grid">
                  <div className="col-12">
                    <strong>Toplam Yanıt:</strong> {surveyResults?.raporlama.total_responses}
                  </div>
                  {surveyResults?.raporlama.response_counts && (
                    <div className="col-12">
                      <h3>Cevap Dağılımı</h3>
                      {renderResponseCounts(surveyResults.raporlama.response_counts)}
                    </div>
                  )}
                </div>
              </Panel>

              <Panel header="Yanıtlar">
                <DataTable value={surveyResults?.raporlama.responses} showGridlines stripedRows paginator rows={10}>
                  <Column field="user_name" header="Kullanıcı"></Column>
                  <Column field="user_osb" header="OSB"></Column>
                  <Column field="result1" header="Cevap"></Column>
                  <Column field="created_at" header="Tarih"></Column>
                </DataTable>
              </Panel>
            </div>
          </TabPanel>

          <TabPanel header="Grafik">
            <div className="flex flex-column gap-3">
              <Panel header="İstatistikler">
                <div className="grid">
                  <div className="col-12">
                    <strong>Toplam Yanıt:</strong> {surveyResults?.grafik.total_responses}
                  </div>
                  {surveyResults?.grafik.response_counts && (
                    <>
                      <div className="col-12 md:col-4">
                        <h3>Soru 1 Dağılımı</h3>
                        {renderResponseCounts(surveyResults.grafik.response_counts.result1)}
                      </div>
                      <div className="col-12 md:col-4">
                        <h3>Soru 2 Dağılımı</h3>
                        {renderResponseCounts(surveyResults.grafik.response_counts.result2)}
                      </div>
                      <div className="col-12 md:col-4">
                        <h3>Soru 3 Dağılımı</h3>
                        {renderResponseCounts(surveyResults.grafik.response_counts.result3)}
                      </div>
                    </>
                  )}
                </div>
              </Panel>

              <Panel header="Yanıtlar">
                <DataTable value={surveyResults?.grafik.responses} showGridlines stripedRows paginator rows={10}>
                  <Column field="user_name" header="Kullanıcı"></Column>
                  <Column field="user_osb" header="OSB"></Column>
                  <Column field="result1" header="Cevap 1"></Column>
                  <Column field="result2" header="Cevap 2"></Column>
                  <Column field="result3" header="Cevap 3"></Column>
                  <Column field="created_at" header="Tarih"></Column>
                </DataTable>
              </Panel>
            </div>
          </TabPanel>

          <TabPanel header="Eğitim">
            <div className="flex flex-column gap-3">
              <Panel header="İstatistikler">
                <div className="grid">
                  <div className="col-12">
                    <strong>Toplam Yanıt:</strong> {surveyResults?.egitim.total_responses}
                  </div>
                  {surveyResults?.egitim.response_counts && (
                    <div className="col-12">
                      <h3>Cevap Dağılımı</h3>
                      {renderResponseCounts(surveyResults.egitim.response_counts)}
                    </div>
                  )}
                </div>
              </Panel>

              <Panel header="Yanıtlar">
                <DataTable value={surveyResults?.egitim.responses} showGridlines stripedRows paginator rows={10}>
                  <Column field="user_name" header="Kullanıcı"></Column>
                  <Column field="user_osb" header="OSB"></Column>
                  <Column field="result1" header="Cevap"></Column>
                  <Column field="created_at" header="Tarih"></Column>
                </DataTable>
              </Panel>
            </div>
          </TabPanel>

          <TabPanel header="İş Sürekliliği">
            <div className="flex flex-column gap-3">
              <Panel header="İstatistikler">
                <div className="grid">
                  <div className="col-12">
                    <strong>Toplam Yanıt:</strong> {surveyResults?.afad.total_responses}
                  </div>
                  {surveyResults?.afad.response_counts && (
                    <div className="col-12">
                      <h3>Cevap Dağılımı</h3>
                      {renderResponseCounts(surveyResults.afad.response_counts)}
                    </div>
                  )}
                </div>
              </Panel>

              <Panel header="Yanıtlar">
                <DataTable value={surveyResults?.afad.responses} showGridlines stripedRows paginator rows={10}>
                  <Column field="user_name" header="Kullanıcı"></Column>
                  <Column field="user_osb" header="OSB"></Column>
                  <Column field="result1" header="Cevap"></Column>
                  <Column field="created_at" header="Tarih"></Column>
                </DataTable>
              </Panel>
            </div>
          </TabPanel>

          <TabPanel header="OSBÜK Bülten">
            <div className="flex flex-column gap-3">
              <Panel header="İstatistikler">
                <div className="grid">
                  <div className="col-12">
                    <strong>Toplam Yanıt:</strong> {surveyResults?.haber.total_responses}
                  </div>
                  {surveyResults?.haber.response_counts && (
                    <div className="col-12">
                      <h3>Cevap Dağılımı</h3>
                      {renderResponseCounts(surveyResults.haber.response_counts)}
                    </div>
                  )}
                </div>
              </Panel>

              <Panel header="Yanıtlar">
                <DataTable value={surveyResults?.haber.responses} showGridlines stripedRows paginator rows={10}>
                  <Column field="user_name" header="Kullanıcı"></Column>
                  <Column field="user_osb" header="OSB"></Column>
                  <Column field="result1" header="Cevap"></Column>
                  <Column field="created_at" header="Tarih"></Column>
                </DataTable>
              </Panel>
            </div>
          </TabPanel>
        </TabView>
      </Card>
    </div>
  );
};

export default SurveyResults;
